import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BreakpointContext } from '@danske/sapphire-react-context';
import { useBreakpointContext } from './useBreakpointContext';
import { BreakpointKey, Breakpoints } from '../utils/useBreakpointTokens';

const SapphireBreakpointProvider = ({
  breakpoints,
  children,
}: {
  breakpoints?: Breakpoints;
  children: React.ReactNode;
}): React.JSX.Element => {
  const existingResolvedBreakpoints = useBreakpointContext();
  const matchedBreakpoints = useMatchedBreakpoints(
    existingResolvedBreakpoints?.breakpoints ?? breakpoints
  );

  return (
    <BreakpointProvider
      breakpoints={existingResolvedBreakpoints?.breakpoints ?? breakpoints}
      matchedBreakpoints={
        existingResolvedBreakpoints?.matchedBreakpoints ?? matchedBreakpoints
      }
    >
      {children}
    </BreakpointProvider>
  );
};

/**
 * Need this because spectrum's "useMatchedBreakpoints" does not react to
 * changing breakpoints. And we don't have the breakpoints from the first
 * render, so it doesn't react to when we get the breakpoints.
 *
 * More info: https://github.com/adobe/react-spectrum/issues/2912
 */
function useMatchedBreakpoints(breakpoints?: Breakpoints): string[] {
  const supportsMatchMedia = useMemo(
    () =>
      typeof window !== 'undefined' && typeof window.matchMedia === 'function',
    []
  );
  const getBreakpointHandler = useCallback(() => {
    const matched = ['base'];

    if (breakpoints) {
      const entries = Object.entries(breakpoints).sort(
        ([, valueA], [, valueB]) => valueB - valueA
      );
      const breakpointQueries = entries.map(
        ([, value]) => `(min-width: ${value}px)`
      );

      for (const i in breakpointQueries) {
        const query = breakpointQueries[i];
        if (window.matchMedia(query).matches) {
          matched.push(entries[i][0]);
        }
      }
    }

    return matched;
  }, [breakpoints, supportsMatchMedia]);

  const [breakpoint, setBreakpoint] = useState(() =>
    supportsMatchMedia ? getBreakpointHandler() : ['base']
  );

  const updateBreakpoints = useCallback(() => {
    if (!supportsMatchMedia) {
      return;
    }

    const breakpointHandler = getBreakpointHandler();

    setBreakpoint((previousBreakpointHandler) => {
      if (
        !previousBreakpointHandler ||
        previousBreakpointHandler.length !== breakpointHandler.length ||
        previousBreakpointHandler.some(
          (breakpoint, idx) => breakpoint !== breakpointHandler[idx]
        )
      ) {
        return [...breakpointHandler]; // Return a new array to force state change
      }

      return previousBreakpointHandler;
    });
  }, [getBreakpointHandler, supportsMatchMedia]);

  useEffect(() => {
    window.addEventListener('resize', updateBreakpoints);
    return () => {
      window.removeEventListener('resize', updateBreakpoints);
    };
  }, [updateBreakpoints]);

  useEffect(() => {
    updateBreakpoints();
  }, [breakpoints, updateBreakpoints]);

  return breakpoint;
}

/**
 * Copy and extend Spectrum BreakpointProvider value with the map of breakpoints
 * https://github.com/adobe/react-spectrum/blob/main/packages/%40react-spectrum/utils/src/BreakpointProvider.tsx
 */

function BreakpointProvider(props: {
  children?: ReactNode;
  matchedBreakpoints?: BreakpointKey[] | ['base'] | string[];
  breakpoints?: Breakpoints;
}) {
  const { children, matchedBreakpoints, breakpoints } = props;
  return (
    <BreakpointContext.Provider value={{ matchedBreakpoints, breakpoints }}>
      {children}
    </BreakpointContext.Provider>
  );
}

export { SapphireBreakpointProvider as BreakpointProvider };
